import zipData from "assets/jsons/stateCityByZip.json";
import {MapPin} from "lucide-react";
import {FC, useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";

const STORAGE_KEY = "tf_onboarded";

const ENGAGEMENT_OPTIONS = ["Full Time", "Part Time", "Freelance Contract"];
const SOFTWARE_OPTIONS = [
  "QuickBooks Online",
  "QuickBooks Desktop",
  "Xero",
  "FreshBooks",
  "MS Excel",
  "SAP",
  "Any / No preference",
];

// --- ZIP lookup (client-side, no API) ---
// Build inverted index once: "austin|tx" -> first ZIP
type ZipEntry = {zip: string; city: string; state: string};

let _index: Record<string, ZipEntry> | null = null;
function getIndex(): Record<string, ZipEntry> {
  if (_index) return _index;
  _index = {};
  for (const [zip, {city, state}] of Object.entries(zipData as Record<string, {city: string; state: string}>)) {
    const key = `${city.toLowerCase()}|${state.toLowerCase()}`;
    if (!_index[key]) _index[key] = {zip, city, state};
  }
  return _index;
}

function findZip(query: string): ZipEntry | null {
  const q = query.trim();
  if (!q) return null;

  // Already a ZIP
  if (/^\d{5}$/.test(q)) {
    const entry = (zipData as Record<string, {city: string; state: string}>)[q];
    return entry ? {zip: q, ...entry} : null;
  }

  const idx = getIndex();

  // "Austin, TX" or "Austin TX"
  const commaIdx = q.indexOf(",");
  const spaceIdx = q.lastIndexOf(" ");
  const splitIdx = commaIdx !== -1 ? commaIdx : spaceIdx !== -1 ? spaceIdx : -1;

  if (splitIdx !== -1) {
    const city = q.slice(0, splitIdx).trim().toLowerCase();
    const state = q
      .slice(splitIdx + 1)
      .trim()
      .toLowerCase();
    if (state.length === 2) {
      // Exact city + state
      const exact = idx[`${city}|${state}`];
      if (exact) return exact;
      // City prefix + exact state
      for (const [key, entry] of Object.entries(idx)) {
        const [kCity, kState] = key.split("|");
        if (kState === state && kCity.startsWith(city)) return entry;
      }
    }
  }

  // City only — prefix match
  const cityQ = q.toLowerCase();
  for (const [key, entry] of Object.entries(idx)) {
    if (key.split("|")[0].startsWith(cityQ)) return entry;
  }

  return null;
}

// --- Component ---
interface Props {
  onDone: () => void;
}

const TalentFinderOnboarding: FC<Props> = ({onDone}) => {
  const [, setSp] = useSearchParams();
  const [step, setStep] = useState(1);
  const [employment, setEmployment] = useState<string[]>([]);
  const [software, setSoftware] = useState<string[]>([]);
  const [location, setLocation] = useState("");
  const [resolved, setResolved] = useState<ZipEntry | null>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  // Debounced lookup as user types
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!location.trim()) {
      setResolved(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setResolved(findZip(location));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [location]);

  const toggle = (list: string[], value: string, set: (v: string[]) => void) => {
    if (value === "Any / No preference") {
      set(list.includes(value) ? [] : ["Any / No preference"]);
      return;
    }
    const next = list.filter((v) => v !== "Any / No preference");
    set(next.includes(value) ? next.filter((v) => v !== value) : [...next, value]);
  };

  const finish = (skipFilters = false) => {
    localStorage.setItem(STORAGE_KEY, "1");
    if (!skipFilters) {
      setSp((old) => {
        if (employment.length) old.set("employment", employment.join(":"));
        if (software.length && !software.includes("Any / No preference")) old.set("software", software.join(":"));
        if (resolved) {
          old.set("zip", resolved.zip);
          old.set("radius", "100");
        }
        return old;
      });
    }
    onDone();
  };

  const OptionButton: FC<{label: string; selected: boolean; onClick: () => void}> = ({label, selected, onClick}) => (
    <button
      onClick={onClick}
      className="w-full text-left px-5 py-4 rounded-2xl border-2 text-sm font-medium transition-all"
      style={
        selected
          ? {borderColor: "#F07340", backgroundColor: "#FEF3EA", color: "#282D4D"}
          : {borderColor: "#E8E4DF", backgroundColor: "white", color: "#282D4D"}
      }
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{backgroundColor: "rgba(0,0,0,0.45)"}}>
      <div className="relative w-full max-w-md mx-4 rounded-3xl overflow-hidden" style={{backgroundColor: "#FBF9F5"}}>
        {/* Progress bar */}
        <div className="h-1.5 w-full" style={{backgroundColor: "#E8E4DF"}}>
          <div
            className="h-full transition-all duration-300"
            style={{width: `${progress}%`, backgroundColor: "#F07340"}}
          />
        </div>

        <div className="px-8 py-8">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{color: "#F07340"}}>
            Step {step} of {totalSteps}
          </p>

          {step === 1 && (
            <>
              <h2 className="text-2xl font-bold mb-1" style={{color: "#282D4D"}}>
                What type of engagement are you looking for?
              </h2>
              <p className="text-sm mb-6" style={{color: "#8490AA"}}>
                Select all that apply.
              </p>
              <div className="flex flex-col gap-3">
                {ENGAGEMENT_OPTIONS.map((opt) => (
                  <OptionButton
                    key={opt}
                    label={opt}
                    selected={employment.includes(opt)}
                    onClick={() => toggle(employment, opt, setEmployment)}
                  />
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-2xl font-bold mb-1" style={{color: "#282D4D"}}>
                Which accounting software should they know?
              </h2>
              <p className="text-sm mb-6" style={{color: "#8490AA"}}>
                Select all that apply.
              </p>
              <div className="flex flex-col gap-3 max-h-72 overflow-y-auto pr-1">
                {SOFTWARE_OPTIONS.map((opt) => (
                  <OptionButton
                    key={opt}
                    label={opt}
                    selected={software.includes(opt)}
                    onClick={() => toggle(software, opt, setSoftware)}
                  />
                ))}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-2xl font-bold mb-1" style={{color: "#282D4D"}}>
                Where are you looking to hire?
              </h2>
              <p className="text-sm mb-6" style={{color: "#8490AA"}}>
                Enter a city, state, or ZIP code. Leave blank for remote/any.
              </p>
              <div
                className="flex items-center gap-2.5 px-4 py-3.5 rounded-2xl border-2 transition-colors"
                style={{
                  borderColor: location && !resolved ? "#F57A47" : resolved ? "#57B98F" : "#3D52AD",
                  backgroundColor: "white",
                }}
              >
                <MapPin className="w-4 h-4 flex-shrink-0" style={{color: "#8490AA"}} />
                <input
                  type="text"
                  value={location}
                  autoFocus
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. Austin, TX or 78701"
                  className="flex-1 text-sm outline-none bg-transparent"
                  style={{color: "#282D4D"}}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") finish();
                  }}
                />
              </div>

              {/* Resolved ZIP feedback */}
              <div className="mt-2 h-4">
                {location.trim() && resolved && (
                  <p className="text-xs font-medium" style={{color: "#57B98F"}}>
                    Using ZIP {resolved.zip} · {resolved.city}, {resolved.state}
                  </p>
                )}
                {location.trim() && !resolved && (
                  <p className="text-xs" style={{color: "#F57A47"}}>
                    Location not found — results won't be filtered by location
                  </p>
                )}
              </div>

              <p className="text-xs mt-3" style={{color: "#8490AA"}}>
                You can always update this later using the Manual Filters.
              </p>
            </>
          )}

          {/* Nav buttons */}
          <div className="flex items-center justify-between mt-8">
            {step === 1 ? (
              <button
                onClick={() => finish(true)}
                className="text-sm font-medium hover:opacity-70 transition-opacity"
                style={{color: "#8490AA"}}
              >
                Skip for now
              </button>
            ) : (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="text-sm font-medium px-5 py-2.5 rounded-xl border border-brand-border hover:border-brand-navy transition-colors"
                style={{color: "#282D4D"}}
              >
                Back
              </button>
            )}

            <button
              onClick={() => (step < totalSteps ? setStep((s) => s + 1) : finish())}
              className="text-sm font-semibold px-7 py-3 rounded-2xl text-white hover:opacity-90 transition-opacity"
              style={{backgroundColor: "#F07340"}}
            >
              {step < totalSteps ? "Next" : "Find Candidates"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export {STORAGE_KEY as TF_ONBOARDED_KEY};
export default TalentFinderOnboarding;
